import { Style } from '@/components/style';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import RuleType from '@/constants/RuleType';
import PhoneUtils from '@/components/customizeWelcome/utils/phoneUtils';
import Validator from '@/utils/validator';
import Area from '@/utils/area';
import {
  setWelcomeAreaCode,
  setWelcomeCustomerPhone,
} from '@/actions/customizeWelcome/customizeWelcomeAction';
import AreaCodeSelector from '@/ui/AreaCodeSelector';
import { ReactComponent as Correct } from '@/assets/img/correct.svg';
import { ReactComponent as Warn } from '@/assets/img/warn.svg';
import { setCurrentSelectedPhone } from '@/actions/memberInfo/memberInfoAction';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import formatTWPhone from '@/utils/phoneUtils/formatTWPhone';

const { alertColor, blackColor, primaryColor, gray3Color } =
  new Style().getUiConfig();

function WelcomePhone({
  webStoreVo,
  selectedServeType,
  selectedTakeOutServeType,
}) {
  const intl = useIntl();
  const dispatch = useDispatch<any>();
  const [areaCodes, setAreaCodes] = useState<any>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<any>(null);
  const [phone, setPhone] = useState<string>('');
  const [isPhoneValid, setPhoneValid] = useState(false);
  const isPhoneRequired = PhoneUtils.checkPhoneRequired(
    webStoreVo,
    selectedServeType,
    selectedTakeOutServeType
  );
  const showPhone = PhoneUtils.showPhoneField(
    webStoreVo,
    selectedServeType,
    selectedTakeOutServeType
  );
  const customerPhone = useSelector<RootStateOrAny, any>(
    (state) => state.customizeWelcomeReducer.customerPhone
  );
  const [pointRuleCondition, setPointRuleCondition] = useState<any>();
  const isCustomerPhoneValid = useSelector(
    (state: RootStateOrAny) =>
      state.customizeWelcomeReducer.isCustomerPhoneValid
  );

  useEffect(() => {
    if (isEmpty(selectedAreaCode) || isEmpty(phone)) return;
    const selectedMemberPhone = { areaCode: selectedAreaCode, phone };
    dispatch(setCurrentSelectedPhone(selectedMemberPhone));
  }, [selectedAreaCode, phone]);

  useEffect(() => {
    setPhoneValid(isCustomerPhoneValid);
  }, [isCustomerPhoneValid]);

  useEffect(() => {
    if (isEmpty(selectedAreaCode)) return;
    dispatch(setWelcomeAreaCode(selectedAreaCode));
  }, [selectedAreaCode]);

  const onChangePhone = (e) => {
    dispatch(setWelcomeCustomerPhone(e.target.value));
    setPhone(e.target.value);
    const PhoneInput = e?.target?.value ? e.target.value : '';
    setPhoneValid(Validator.phoneValidator(PhoneInput, 'TW', isPhoneRequired)); // TODO : 不同國家的檢查
  };

  const getPhoneErrorFormatMessage = () => {
    const phoneElement: any = document.getElementById('phone');
    if (isEmpty(phoneElement?.value)) {
      return intl.formatMessage({
        id: 'cart.customerInfo.phone.empty.error',
      });
    }
    return intl.formatMessage({
      id: 'error.invalidMobile',
    });
  };

  const getPointIssueRule = () => {
    const rule = webStoreVo.genericRules.find(
      (r) => r.type === RuleType.PRICE_POINT || r.type === RuleType.BUY_M_POINT
    );
    setPointRuleCondition(rule);
    return rule;
  };

  useEffect(() => {
    if (isEmpty(webStoreVo)) return;
    const tempAreaCodes = Area.getAreaCodesListByLangs(webStoreVo?.langs);
    setAreaCodes(tempAreaCodes);
    setSelectedAreaCode(tempAreaCodes[0]);
    if (webStoreVo?.isRoyaltyEnable) {
      getPointIssueRule();
    }
  }, [webStoreVo]);

  const isMemberLogin = useSelector<RootStateOrAny, any>(
    (state) => state.memberInfoReducer.isLogin
  );
  const memberProfileData = useSelector<RootStateOrAny, any>(
    (state) => state.memberInfoReducer
  );
  const checkPhoneIsDisabled = () => {
    const phoneElement: any = document.getElementById('phone');
    const areaCodeDropDownMenuElement: any = document.getElementById(
      'areaCodeDropDownMenu'
    );

    if (isMemberLogin) {
      if (phoneElement) {
        phoneElement.disabled = true;
      }
      if (areaCodeDropDownMenuElement) {
        areaCodeDropDownMenuElement.disabled = true;
      }
    } else {
      if (phoneElement) {
        phoneElement.disabled = false;
      }
      if (areaCodeDropDownMenuElement) {
        areaCodeDropDownMenuElement.disabled = false;
      }
    }
  };

  useEffect(() => {
    checkPhoneIsDisabled();
  }, [isMemberLogin]);

  useEffect(() => {
    // if (!isMemberLogin) return;
    if (isEmpty(memberProfileData)) return;
    if (!isEmpty(memberProfileData?.areaCode)) {
      setSelectedAreaCode(memberProfileData.areaCode);
      dispatch(setWelcomeAreaCode(memberProfileData.areaCode));
    }

    if (!isEmpty(memberProfileData?.phone)) {
      setPhone(formatTWPhone(memberProfileData.phone));
      dispatch(setWelcomeCustomerPhone(formatTWPhone(memberProfileData.phone)));
    }
  }, [memberProfileData, isMemberLogin]);
  return (
    <div>
      {showPhone && (
        <div>
          <div
            className="gap-2 d-flex justify-content-between"
            style={{ position: 'relative', height: '44px' }}
          >
            <AreaCodeSelector
              setAreaCode={setSelectedAreaCode}
              areaCode={selectedAreaCode}
              areaCodeList={areaCodes}
            />
            <input
              className="phone"
              style={{
                border: `1px solid ${isPhoneValid ? gray3Color : alertColor}`,
                color: `${isPhoneValid ? blackColor : alertColor}`,
                padding: '10px',
              }}
              type="tel"
              id="phone"
              data-testid="phone"
              placeholder={
                isPhoneRequired
                  ? intl.formatMessage({
                      id: 'cart.customerInfo.phone.required',
                    })
                  : intl.formatMessage({
                      id: 'cart.customerInfo.phone',
                    })
              }
              value={phone}
              onChange={onChangePhone}
            />
          </div>

          {!pointRuleCondition && isPhoneValid && (
            <div style={{ marginBottom: '10px' }} />
          )}

          {!isPhoneValid && (
            <div
              className="d-flex align-items-center"
              style={{
                color: alertColor,
                fontSize: '12px',
                margin: '5px 0px 10px 0px',
              }}
            >
              <Warn style={{ marginRight: '5px' }} />
              {getPhoneErrorFormatMessage()}
            </div>
          )}
          {isPhoneValid && webStoreVo?.isRoyaltyEnable && pointRuleCondition && (
            <div
              style={{
                color: primaryColor,
                fontSize: '12px',
                margin: '5px 0px 10px 0px',
              }}
            >
              {phone ? (
                <div className="d-flex align-items-center">
                  <Correct style={{ marginRight: '5px' }} />
                  {intl.formatMessage({
                    id: 'cart.customerInfo.phone.royaltyEnable.valid',
                  })}
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  <FormattedMessage id="welcome.phone.royaltyEnable.reminder" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

WelcomePhone.defaultProps = {};

WelcomePhone.propTypes = {
  webStoreVo: PropTypes.shape({
    availableServeTypes: PropTypes.arrayOf(PropTypes.string),
    availableTakeOutServeTypes: PropTypes.arrayOf(PropTypes.string),
    langs: PropTypes.any,
    isRoyaltyEnable: PropTypes.bool,
  }).isRequired,
  selectedServeType: PropTypes.string.isRequired,
  selectedTakeOutServeType: PropTypes.string.isRequired,
};

export default WelcomePhone;
